import React from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { Node, mergeAttributes } from '@tiptap/core'
import { Suggestion } from '@tiptap/suggestion'
import { ReactRenderer } from '@tiptap/react'
import tippy from 'tippy.js'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import { Box } from '@mui/material'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'
import { makeStyles } from '@material-ui/core'
import SuggestionList from './SuggestionList'
import './TipTapEditor.css'

// Create dynamic placeholder extension. Defined outside the component (and memoized
// at the call site) so it isn't rebuilt - and the editor's extensions array isn't
// invalidated - on every keystroke.
const createCustomPlaceholder = (placeholderItems) => {
  return Node.create({
    name: 'customPlaceholder',

    addOptions() {
      return {
        HTMLAttributes: {},
        renderText({ node }) {
          return `@@${node.attrs.label}@@`
        },
        suggestion: {
          char: '@@',
          allowSpaces: false,
          allowedPrefixes: [' ', '\n'],
          startOfLine: false,

          items: ({ query }) => {
            return placeholderItems
              .filter(
                (item) =>
                  item.label.toLowerCase().includes(query.toLowerCase()) ||
                  item.description.toLowerCase().includes(query.toLowerCase())
              )
              .slice(0, 8)
          },

          allow: ({ editor: _editor, state, range: _range }) => {
            const { $from } = state.selection
            const type = $from.parent.type

            // Disable in code blocks, code inline, and links
            if (
              type.name === 'codeBlock' ||
              type.name === 'code' ||
              $from.marks().some((mark) => mark.type.name === 'link')
            ) {
              return false
            }

            return true
          },

          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .insertContentAt(range, {
                type: 'customPlaceholder',
                attrs: {
                  id: props.id,
                  label: props.label
                }
              })
              .run()
          },

          render: () => {
            let component
            let popup

            return {
              onStart: (props) => {
                component = new ReactRenderer(SuggestionList, {
                  props,
                  editor: props.editor
                })

                if (!props.clientRect) {
                  return
                }

                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start'
                })
              },

              onUpdate(props) {
                component.updateProps(props)

                if (!props.clientRect) {
                  return
                }

                popup[0].setProps({
                  getReferenceClientRect: props.clientRect
                })
              },

              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  popup[0].hide()
                  return true
                }

                return component.ref?.onKeyDown?.(props)
              },

              onExit() {
                popup?.[0]?.destroy()
                component?.destroy()
              }
            }
          }
        }
      }
    },

    group: 'inline',

    inline: true,

    selectable: false,

    atom: true,

    addAttributes() {
      return {
        id: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-id'),
          renderHTML: (attributes) => {
            if (!attributes.id) return {}
            return { 'data-id': attributes.id }
          }
        },
        label: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-label'),
          renderHTML: (attributes) => {
            if (!attributes.label) return {}
            return { 'data-label': attributes.label }
          }
        }
      }
    },

    parseHTML() {
      return [{ tag: 'span[data-type="customPlaceholder"]' }]
    },

    renderHTML({ node, HTMLAttributes }) {
      return [
        'span',
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
          'data-type': 'customPlaceholder',
          'data-id': node.attrs.id,
          'data-label': node.attrs.label,
          class: 'custom-placeholder',
          contenteditable: 'false'
        }),
        this.options.renderText({ node })
      ]
    },

    renderText({ node }) {
      return this.options.renderText({ node })
    },

    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          ...this.options.suggestion
        })
      ]
    }
  })
}

const ToolbarButton = ({
  classes,
  onClick,
  isActive,
  children,
  title,
  className: btnClassName = '',
  disabled = false
}) => (
  <button
    type="button"
    onClick={onClick}
    onMouseDown={(e) => e.preventDefault()}
    className={`${classes.toolbarButton} ${
      isActive ? classes.toolbarButtonActive : ''
    } ${btnClassName}`}
    title={title}
    disabled={disabled}>
    {children}
  </button>
)

const ColorPicker = ({ classes, editor, disabled }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const dropdownRef = React.useRef(null)
  const colors = [
    '#000000',
    '#DC143C',
    '#FF8C00',
    '#FFD700',
    '#32CD32',
    '#1E90FF',
    '#8A2BE2',
    '#808080'
  ]

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className={classes.dropdownContainer} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onMouseDown={(e) => e.preventDefault()}
        className={`${classes.colorPickerButton} ${isOpen ? classes.dropdownButtonActive : ''}`}
        title="Text Color"
        disabled={disabled}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
          <svg width="16" height="14" viewBox="0 0 24 20" fill="currentColor">
            <path d="M11 2L5.5 16h2.25l1.12-3h6.25l1.12 3H18.5L13 2h-2zm-1.38 9L12 4.67 14.38 11H9.62z" />
          </svg>
          <div
            className={classes.colorIndicator}
            style={{ backgroundColor: editor?.getAttributes('textStyle')?.color || '#000000' }}
          />
        </div>
      </button>
      {isOpen && (
        <div className={classes.colorPickerMenu}>
          <div className={classes.colorGrid}>
            {colors.map((color) => (
              <button
                key={color}
                type="button"
                className={classes.colorSwatch}
                style={{ backgroundColor: color }}
                onClick={() => {
                  editor.chain().focus().setColor(color).run()
                  setIsOpen(false)
                }}
                onMouseDown={(e) => e.preventDefault()}
                title={color}
              />
            ))}
          </div>
          <div className={classes.colorActions}>
            <button
              type="button"
              className={classes.colorAction}
              onClick={() => {
                editor.chain().focus().unsetColor().run()
                setIsOpen(false)
              }}
              onMouseDown={(e) => e.preventDefault()}>
              Remove Color
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const HeadingDropdown = ({ classes, editor, disabled }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [dropdownPosition, setDropdownPosition] = React.useState('bottom')
  const dropdownRef = React.useRef(null)

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  React.useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect()
      const windowHeight = window.innerHeight
      const spaceBelow = windowHeight - rect.bottom
      const spaceAbove = rect.top

      // If there's more space above than below and below is insufficient, show on top
      if (spaceBelow < 300 && spaceAbove > spaceBelow) {
        setDropdownPosition('top')
      } else {
        setDropdownPosition('bottom')
      }
    }
  }, [isOpen])

  const getCurrentHeading = () => {
    if (!editor) return 'Text'
    if (editor.isActive('heading', { level: 1 })) return 'H1'
    if (editor.isActive('heading', { level: 2 })) return 'H2'
    if (editor.isActive('heading', { level: 3 })) return 'H3'
    if (editor.isActive('heading', { level: 4 })) return 'H4'
    if (editor.isActive('heading', { level: 5 })) return 'H5'
    if (editor.isActive('heading', { level: 6 })) return 'H6'
    return 'Text'
  }

  return (
    <div className={classes.dropdownContainer} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onMouseDown={(e) => e.preventDefault()}
        className={`${classes.dropdownButton} ${isOpen ? classes.dropdownButtonActive : ''}`}
        disabled={disabled}>
        {getCurrentHeading()}
        <svg className={classes.dropdownArrow} width="12" height="12" viewBox="0 0 12 12">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>
      {isOpen && (
        <div
          className={`${classes.dropdownMenu} ${
            dropdownPosition === 'top' ? classes.dropdownMenuTop : classes.dropdownMenuBottom
          }`}>
          <button
            type="button"
            onClick={() => {
              editor.chain().focus().setParagraph().run()
              setIsOpen(false)
            }}
            onMouseDown={(e) => e.preventDefault()}
            className={`${classes.dropdownItem} ${
              !editor.isActive('heading') ? classes.dropdownItemActive : ''
            }`}>
            <div className={classes.dropdownItemContent}>
              <span>Text</span>
              <span className={classes.dropdownItemDesc}>Regular paragraph</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 1 }).run()
              setIsOpen(false)
            }}
            onMouseDown={(e) => e.preventDefault()}
            className={`${classes.dropdownItem} ${
              editor.isActive('heading', { level: 1 }) ? classes.dropdownItemActive : ''
            }`}>
            <div className={classes.dropdownItemContent}>
              <span className={classes.h1Preview}>H1</span>
              <span className={classes.dropdownItemDesc}>Large heading</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 2 }).run()
              setIsOpen(false)
            }}
            onMouseDown={(e) => e.preventDefault()}
            className={`${classes.dropdownItem} ${
              editor.isActive('heading', { level: 2 }) ? classes.dropdownItemActive : ''
            }`}>
            <div className={classes.dropdownItemContent}>
              <span className={classes.h2Preview}>H2</span>
              <span className={classes.dropdownItemDesc}>Medium heading</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 3 }).run()
              setIsOpen(false)
            }}
            onMouseDown={(e) => e.preventDefault()}
            className={`${classes.dropdownItem} ${
              editor.isActive('heading', { level: 3 }) ? classes.dropdownItemActive : ''
            }`}>
            <div className={classes.dropdownItemContent}>
              <span className={classes.h3Preview}>H3</span>
              <span className={classes.dropdownItemDesc}>Small heading</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 4 }).run()
              setIsOpen(false)
            }}
            onMouseDown={(e) => e.preventDefault()}
            className={`${classes.dropdownItem} ${
              editor.isActive('heading', { level: 4 }) ? classes.dropdownItemActive : ''
            }`}>
            <div className={classes.dropdownItemContent}>
              <span className={classes.h4Preview}>H4</span>
              <span className={classes.dropdownItemDesc}>Smaller heading</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 5 }).run()
              setIsOpen(false)
            }}
            onMouseDown={(e) => e.preventDefault()}
            className={`${classes.dropdownItem} ${
              editor.isActive('heading', { level: 5 }) ? classes.dropdownItemActive : ''
            }`}>
            <div className={classes.dropdownItemContent}>
              <span className={classes.h5Preview}>H5</span>
              <span className={classes.dropdownItemDesc}>Tiny heading</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => {
              editor.chain().focus().toggleHeading({ level: 6 }).run()
              setIsOpen(false)
            }}
            onMouseDown={(e) => e.preventDefault()}
            className={`${classes.dropdownItem} ${
              editor.isActive('heading', { level: 6 }) ? classes.dropdownItemActive : ''
            }`}>
            <div className={classes.dropdownItemContent}>
              <span className={classes.h6Preview}>H6</span>
              <span className={classes.dropdownItemDesc}>Smallest heading</span>
            </div>
          </button>
        </div>
      )}
    </div>
  )
}

const TipTapEditor = ({
  value = '',
  onChange,
  placeholder = 'Start typing...',
  disabled = false,
  onBlur,
  error = false,
  minHeight = '120px',
  maxHeight = '300px',
  hasPlaceholders = false,
  placeholders = []
}) => {
  const classes = useStyles({ minHeight, maxHeight, error })

  const extensions = React.useMemo(
    () =>
      [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3, 4, 5, 6]
          }
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            rel: 'noopener noreferrer',
            target: '_blank'
          }
        }),
        TextStyle,
        Color,
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph']
        }),
        ...(hasPlaceholders ? [createCustomPlaceholder(placeholders)] : [])
      ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasPlaceholders]
  )

  const editor = useEditor({
    extensions,
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      if (onChange) {
        const html = editor.getHTML()
        onChange(html)
      }
    },
    onBlur: ({ editor }) => {
      if (onBlur) {
        onBlur(editor.getHTML())
      }
    },
    editorProps: {
      attributes: {
        class: classes.proseMirror,
        'data-placeholder': placeholder
      }
    }
  })

  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false)
    }
  }, [value, editor])

  React.useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [disabled, editor])

  if (!editor) return null

  return (
    <Box className={classes.editorWrapper}>
      <div className={classes.toolbar}>
        {/* Undo/Redo Section */}
        <div className={classes.toolbarSection}>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().undo().run()}
            isActive={false}
            title="Undo (⌘Z)"
            disabled={!editor.can().undo()}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12.5,8C9.85,8 7.45,9 5.6,10.6L2,7V16H11L7.38,12.38C8.77,11.22 10.54,10.5 12.5,10.5C16.04,10.5 19.05,12.81 20.1,16L22.47,15.22C21.08,11.03 17.15,8 12.5,8Z" />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().redo().run()}
            isActive={false}
            title="Redo (⌘⇧Z)"
            disabled={!editor.can().redo()}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.4,10.6C16.55,9 14.15,8 11.5,8C6.85,8 2.92,11.03 1.53,15.22L3.9,16C4.95,12.81 7.96,10.5 11.5,10.5C13.46,10.5 15.23,11.22 16.62,12.38L13,16H22V7L18.4,10.6Z" />
            </svg>
          </ToolbarButton>
        </div>
        <div className={classes.toolbarDivider} />

        {/* Text Format Dropdown */}
        <div className={classes.toolbarSection}>
          <HeadingDropdown classes={classes} editor={editor} disabled={disabled} />
        </div>
        <div className={classes.toolbarDivider} />

        {/* Text Formatting */}
        <div className={classes.toolbarSection}>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title="Bold (⌘B)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.5,15.5H10V12.5H13.5A1.5,1.5 0 0,1 15,14A1.5,1.5 0 0,1 13.5,15.5M10,6.5H13A1.5,1.5 0 0,1 14.5,8A1.5,1.5 0 0,1 13,9.5H10M15.6,10.79C16.57,10.11 17.25,9.02 17.25,8C17.25,5.74 15.5,4 13.25,4H7V18H14.04C16.14,18 17.75,16.3 17.75,14.21C17.75,12.69 16.89,11.39 15.6,10.79Z" />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title="Italic (⌘I)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10,4V7H12.21L8.79,15H6V18H14V15H11.79L15.21,7H18V4H10Z" />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().toggleUnderline?.().run()}
            isActive={editor.isActive('underline')}
            title="Underline (⌘U)">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M5,21H19V19H5V21M12,17A6,6 0 0,0 18,11V3H15.5V11A3.5,3.5 0 0,1 12,14.5A3.5,3.5 0 0,1 8.5,11V3H6V11A6,6 0 0,0 12,17Z" />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
            title="Strikethrough">
            <StrikethroughSIcon style={{ width: 18, height: 18 }} />
          </ToolbarButton>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().toggleCode().run()}
            isActive={editor.isActive('code')}
            title="Inline Code">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14.6,16.6L19.2,12L14.6,7.4L16,6L22,12L16,18L14.6,16.6M9.4,16.6L4.8,12L9.4,7.4L8,6L2,12L8,18L9.4,16.6Z" />
            </svg>
          </ToolbarButton>
          <ColorPicker classes={classes} editor={editor} disabled={disabled} />
        </div>
        <div className={classes.toolbarDivider} />

        {/* Lists */}
        <div className={classes.toolbarSection}>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title="Bullet List">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7,5H21V7H7V5M7,13V11H21V13H7M4,4.5A1.5,1.5 0 0,1 5.5,6A1.5,1.5 0 0,1 4,7.5A1.5,1.5 0 0,1 2.5,6A1.5,1.5 0 0,1 4,4.5M4,10.5A1.5,1.5 0 0,1 5.5,12A1.5,1.5 0 0,1 4,13.5A1.5,1.5 0 0,1 2.5,12A1.5,1.5 0 0,1 4,10.5M7,19V17H21V19H7M4,16.5A1.5,1.5 0 0,1 5.5,18A1.5,1.5 0 0,1 4,19.5A1.5,1.5 0 0,1 2.5,18A1.5,1.5 0 0,1 4,16.5Z" />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title="Numbered List">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7,13V11H21V13H7M7,19V17H21V19H7M7,7V5H21V7H7M3,8V5H2V4H4V8H3M2,17V16H5V20H2V19H4V18.5H3V18H4V17.5H2V17M4.25,10A0.75,0.75 0 0,1 5,10.75C5,10.95 4.92,11.14 4.79,11.27L3.12,13H5V14H2V13.08L4,11H2V10H4.25Z" />
            </svg>
          </ToolbarButton>
        </div>
        <div className={classes.toolbarDivider} />

        {/* Text Alignment */}
        <div className={classes.toolbarSection}>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            isActive={editor.isActive({ textAlign: 'left' })}
            title="Align Left">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,3H21V5H3V3M3,7H15V9H3V7M3,11H21V13H3V11M3,15H15V17H3V15M3,19H21V21H3V19Z" />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            isActive={editor.isActive({ textAlign: 'center' })}
            title="Align Center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,3H21V5H3V3M7,7H17V9H7V7M3,11H21V13H3V11M7,15H17V17H7V15M3,19H21V21H3V19Z" />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            isActive={editor.isActive({ textAlign: 'right' })}
            title="Align Right">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,3H21V5H3V3M9,7H21V9H9V7M3,11H21V13H3V11M9,15H21V17H9V15M3,19H21V21H3V19Z" />
            </svg>
          </ToolbarButton>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            isActive={editor.isActive({ textAlign: 'justify' })}
            title="Justify">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,3H21V5H3V3M3,7H21V9H3V7M3,11H21V13H3V11M3,15H21V17H3V15M3,19H21V21H3V19Z" />
            </svg>
          </ToolbarButton>
        </div>
        <div className={classes.toolbarDivider} />

        {/* Blockquote */}
        <div className={classes.toolbarSection}>
          <ToolbarButton
            classes={classes}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            title="Blockquote">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z" />
            </svg>
          </ToolbarButton>
        </div>
        <div className={classes.toolbarDivider} />

        {/* Link */}
        <div className={classes.toolbarSection}>
          <ToolbarButton
            classes={classes}
            onClick={() => {
              // Check if the current selection has a link
              if (editor.isActive('link')) {
                // If there's already a link, remove it
                editor.chain().focus().unsetLink().run()
              } else {
                // If no link, prompt for URL and set link
                const url = window.prompt('Enter URL:')
                if (url) {
                  editor.chain().focus().setLink({ href: url }).run()
                }
              }
            }}
            isActive={editor.isActive('link')}
            title={editor.isActive('link') ? 'Remove Link' : 'Add Link'}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.9,12C3.9,10.29 5.29,8.9 7,8.9H11V7H7A5,5 0 0,0 2,12A5,5 0 0,0 7,17H11V15.1H7C5.29,15.1 3.9,13.71 3.9,12M8,13H16V11H8V13M17,7H13V8.9H17C18.71,8.9 20.1,10.29 20.1,12C20.1,13.71 18.71,15.1 17,15.1H13V17H17A5,5 0 0,0 22,12A5,5 0 0,0 17,7Z" />
            </svg>
          </ToolbarButton>
        </div>

        {hasPlaceholders && (
          <>
            <div className={classes.toolbarSpacer} />
            <div className={classes.placeholderHint}>
              <span>
                Type <code>@@</code> to insert placeholders
              </span>
            </div>
          </>
        )}
      </div>
      <div className={classes.editorContainer}>
        <EditorContent editor={editor} />
      </div>
    </Box>
  )
}

const useStyles = makeStyles(() => ({
  editorWrapper: {
    border: ({ error }) => `1px solid ${error ? '#f44336' : '#d0d7de'}`,
    borderRadius: '8px',
    backgroundColor: '#fff',
    overflow: 'hidden',
    boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.05)',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      borderColor: ({ error }) => (error ? '#f44336' : '#8c959f')
    },
    '&:focus-within': {
      borderColor: ({ error }) => (error ? '#f44336' : '#0969da'),
      boxShadow: ({ error }) =>
        error ? '0 0 0 3px rgba(244, 67, 54, 0.1)' : '0 0 0 3px rgba(9, 105, 218, 0.1)'
    }
  },

  toolbar: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    borderBottom: '1px solid #d8dee4',
    backgroundColor: '#f6f8fa',
    gap: '3px',
    minHeight: '40px'
  },

  toolbarSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px'
  },

  toolbarDivider: {
    width: '1px',
    height: '20px',
    backgroundColor: '#d8dee4',
    margin: '0 4px',
    flexShrink: 0
  },

  toolbarSpacer: {
    flex: 1
  },
  placeholderHint: {
    marginLeft: 'auto',
    fontSize: '0.75rem',
    color: '#666',
    '& code': {
      padding: '0.125rem 0.25rem',
      backgroundColor: '#f5f5f5',
      borderRadius: '0.25rem',
      fontFamily: 'monospace'
    }
  },

  dropdownContainer: {
    position: 'relative'
  },

  dropdownButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 6px',
    border: '1px solid #d8dee4',
    borderRadius: '4px',
    backgroundColor: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    color: '#24292f',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    minWidth: '60px',
    height: '28px',
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: '#f3f4f6',
      borderColor: '#8c959f'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },

  dropdownButtonActive: {
    backgroundColor: '#f3f4f6',
    borderColor: '#8c959f'
  },

  dropdownArrow: {
    transition: 'transform 0.15s ease',
    color: '#656d76'
  },

  dropdownMenu: {
    position: 'absolute',
    left: 0,
    backgroundColor: '#fff',
    border: '1px solid #d8dee4',
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    minWidth: '220px',
    maxWidth: '280px',
    padding: '6px',
    maxHeight: '140px',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: '#d0d7de #f6f8fa',
    '&::-webkit-scrollbar': {
      width: '8px'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f6f8fa',
      borderRadius: '4px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#d0d7de',
      borderRadius: '4px',
      '&:hover': {
        background: '#8c959f'
      }
    }
  },

  dropdownMenuBottom: {
    top: '100%',
    marginTop: '4px'
  },

  dropdownMenuTop: {
    bottom: '100%',
    marginBottom: '4px'
  },

  dropdownItem: {
    display: 'block',
    width: '100%',
    padding: '8px 12px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#f3f4f6'
    }
  },

  dropdownItemActive: {
    backgroundColor: '#dbeafe',
    '&:hover': {
      backgroundColor: '#bfdbfe'
    }
  },

  dropdownItemContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px'
  },

  dropdownItemDesc: {
    fontSize: '12px',
    color: '#656d76'
  },

  h1Preview: {
    fontSize: '16px',
    fontWeight: '700'
  },

  h2Preview: {
    fontSize: '15px',
    fontWeight: '600'
  },

  h3Preview: {
    fontSize: '14px',
    fontWeight: '600'
  },

  h4Preview: {
    fontSize: '13px',
    fontWeight: '600'
  },

  h5Preview: {
    fontSize: '12px',
    fontWeight: '600'
  },

  h6Preview: {
    fontSize: '11px',
    fontWeight: '600'
  },

  toolbarButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    border: '1px solid transparent',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    color: '#24292f',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    width: '28px',
    height: '28px',
    fontSize: '14px',
    '& svg': {
      width: '18px',
      height: '18px'
    },
    '&:hover': {
      backgroundColor: '#f3f4f6',
      borderColor: '#d8dee4'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },

  toolbarButtonActive: {
    backgroundColor: '#0969da',
    borderColor: '#0969da',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#0860ca'
    }
  },

  colorPickerButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '4px 6px',
    border: '1px solid #d8dee4',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#24292f',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    minWidth: '36px',
    height: '28px',
    justifyContent: 'center',
    fontSize: '14px',
    '& svg': {
      width: '18px',
      height: '18px'
    },
    '&:hover': {
      backgroundColor: '#f3f4f6',
      borderColor: '#8c959f'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },

  colorIndicator: {
    width: '12px',
    height: '3px',
    borderRadius: '1px',
    border: '1px solid rgba(0,0,0,0.1)'
  },

  colorPickerMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: '4px',
    backgroundColor: '#fff',
    border: '1px solid #d8dee4',
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    padding: '12px',
    minWidth: '180px'
  },

  colorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '6px',
    marginBottom: '8px'
  },

  colorSwatch: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: '1px solid rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      transform: 'scale(1.1)',
      borderColor: '#0969da'
    }
  },

  colorActions: {
    borderTop: '1px solid #d8dee4',
    paddingTop: '8px'
  },

  colorAction: {
    display: 'block',
    width: '100%',
    padding: '6px 8px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#656d76',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#f3f4f6',
      color: '#24292f'
    }
  },

  editorContainer: {
    minHeight: ({ minHeight }) => minHeight,
    maxHeight: ({ maxHeight }) => maxHeight,
    overflow: 'auto'
  },

  proseMirror: {
    padding: '16px',
    outline: 'none !important',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#24292f',
    '&:focus': {
      outline: 'none !important'
    },
    '&[data-placeholder]:empty:before': {
      content: 'attr(data-placeholder)',
      color: '#656d76',
      pointerEvents: 'none',
      height: 0,
      float: 'left'
    },
    '& p': {
      margin: '0 0 8px 0',
      '&:last-child': {
        marginBottom: 0
      }
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontWeight: '600',
      lineHeight: '1.4',
      margin: '16px 0 8px 0',
      '&:first-child': {
        marginTop: 0
      }
    },
    '& h1': {
      fontSize: '24px'
    },
    '& h2': {
      fontSize: '20px'
    },
    '& h3': {
      fontSize: '16px'
    },
    '& h4': {
      fontSize: '14px'
    },
    '& h5': {
      fontSize: '12px'
    },
    '& h6': {
      fontSize: '11px'
    },
    '& ul, & ol': {
      paddingLeft: '24px',
      margin: '8px 0'
    },
    '& li': {
      margin: '4px 0'
    },
    '& ul': {
      listStyle: 'disc'
    },
    '& ol': {
      listStyle: 'decimal'
    },
    '& a': {
      color: '#0969da',
      textDecoration: 'underline',
      '&:hover': {
        textDecoration: 'none'
      }
    },
    '& strong': {
      fontWeight: '600'
    },
    '& em': {
      fontStyle: 'italic'
    },
    '& s': {
      textDecoration: 'line-through'
    },
    '& code': {
      padding: '2px 4px',
      backgroundColor: '#f6f8fa',
      border: '1px solid #d8dee4',
      borderRadius: '4px',
      fontSize: '85%',
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace'
    },
    '& pre': {
      backgroundColor: '#f6f8fa',
      border: '1px solid #d8dee4',
      borderRadius: '8px',
      padding: '16px',
      margin: '16px 0',
      overflow: 'auto',
      fontSize: '85%',
      lineHeight: '1.45',
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace'
    },
    '& blockquote': {
      borderLeft: '4px solid #d8dee4',
      paddingLeft: '16px',
      margin: '16px 0',
      color: '#656d76',
      fontStyle: 'italic'
    },
    '& hr': {
      border: 'none',
      height: '1px',
      backgroundColor: '#d8dee4',
      margin: '24px 0'
    },
    '& u': {
      textDecoration: 'underline'
    }
  }
}))

export default TipTapEditor
